import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import { useVirtualizer } from '@localagent/ui/lib/virtualizer';
import {
  selectionInGroup,
  type SearchResult,
  type Session,
  type SessionListFlatRow,
  type SessionListView,
  type SessionRef,
  type SessionWorkItem,
} from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';
import { SessionRow } from './SessionRow';
import type { AgentSession } from '../../types/session-types';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export interface SessionListBodyProps {
  mode: 'browse' | 'search';
  rows: SessionListFlatRow<Session>[];
  searchHits: SearchResult[];
  view: SessionListView<Session> | null;
  workItemsBySession: ReadonlyMap<string, SessionWorkItem[]>;
  collapsedKeys: ReadonlySet<string>;
  selectedIds: ReadonlySet<string>;
  selecting: boolean;
  activeSessionId: string | undefined;
  deleting: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  listError: boolean;
  isEmpty: boolean;
  noMatches: boolean;
  onToggleCollapsed: (key: string) => void;
  onToggleSelect: (ref: SessionRef) => void;
  onSelectGroup: (refs: SessionRef[]) => void;
  onOpenSession: (sessionId: string) => void;
  onDeleteRow: (
    sessionId: string,
    workspaceId: string | null | undefined,
    event: MouseEvent,
  ) => void;
  onLoadMore: () => void;
}

function asAgentSession(session: Session): AgentSession {
  return session as AgentSession;
}

export function SessionListBody({
  mode,
  rows,
  searchHits,
  view,
  workItemsBySession,
  collapsedKeys,
  selectedIds,
  selecting,
  activeSessionId,
  deleting,
  loading,
  loadingMore,
  hasMore,
  listError,
  isEmpty,
  noMatches,
  onToggleCollapsed,
  onToggleSelect,
  onSelectGroup,
  onOpenSession,
  onDeleteRow,
  onLoadMore,
}: SessionListBodyProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const itemCount = mode === 'search' ? searchHits.length : rows.length;
  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastIndex = virtualItems.at(-1)?.index ?? 0;
  useEffect(() => {
    if (hasMore && !loadingMore && itemCount > 0 && lastIndex >= itemCount - 8) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, itemCount, lastIndex, onLoadMore]);

  return (
    <div ref={parentRef} className="flex-1 overflow-y-auto" data-testid="session-list">
      {loading && (
        <LoadingState variant="inline" label="Loading..." className="justify-center p-3" />
      )}
      {listError && (
        <div className="p-3 text-center text-sm text-destructive" data-testid="session-list-error">
          Failed to load sessions
        </div>
      )}
      {isEmpty && (
        <div className="p-3 text-center text-sm text-muted-foreground" data-testid="session-list-empty">
          No conversations yet
        </div>
      )}
      {noMatches && (
        <div
          className="p-3 text-center text-sm text-muted-foreground"
          data-testid="session-list-no-matches"
        >
          No sessions match the current filter
        </div>
      )}

      <div
        style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}
      >
        {virtualItems.map((virtualRow) => {
          const style = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
          };
          if (mode === 'search') {
            const hit = searchHits[virtualRow.index];
            if (!hit) return null;
            return (
              <div
              key={hit.entity_id}
              ref={virtualizer.measureElement}
              style={style}
              data-index={virtualRow.index}
            >
                <SearchHitRow
                  hit={hit}
                  selecting={selecting}
                  selected={selectedIds.has(hit.entity_id)}
                  isActive={activeSessionId === hit.entity_id}
                  onToggleSelect={onToggleSelect}
                  onOpenSession={onOpenSession}
                />
              </div>
            );
          }
          const row = rows[virtualRow.index];
          if (!row) return null;
          return (
            <div
              key={`${row.type}-${row.type === 'session' ? row.session.id : row.key}`}
              ref={virtualizer.measureElement}
              style={style}
              data-index={virtualRow.index}
            >
              <BrowseRow
                row={row}
                view={view}
                workItemsBySession={workItemsBySession}
                collapsedKeys={collapsedKeys}
                selectedIds={selectedIds}
                selecting={selecting}
                activeSessionId={activeSessionId}
                deleting={deleting}
                onToggleCollapsed={onToggleCollapsed}
                onToggleSelect={onToggleSelect}
                onSelectGroup={onSelectGroup}
                onOpenSession={onOpenSession}
                onDeleteRow={onDeleteRow}
              />
            </div>
          );
        })}
      </div>

      {(loadingMore || hasMore) && (
        <div
          className="border-t px-3 py-1.5 text-[11px] text-muted-foreground"
          data-testid="session-list-footer"
        >
          {loadingMore ? 'Loading…' : 'More available'}
        </div>
      )}
    </div>
  );
}

function SearchHitRow({
  hit,
  selecting,
  selected,
  isActive,
  onToggleSelect,
  onOpenSession,
}: {
  hit: SearchResult;
  selecting: boolean;
  selected: boolean;
  isActive: boolean;
  onToggleSelect: (ref: SessionRef) => void;
  onOpenSession: (sessionId: string) => void;
}) {
  const ref: SessionRef = {
    session_id: hit.entity_id,
    workspace_id: hit.workspace_id ?? null,
  };
  return (
    <div
      className={`flex cursor-pointer items-center px-3 py-2 text-sm hover:bg-accent ${
        isActive ? 'font-semibold' : ''
      }`}
      data-testid={`session-item-${hit.entity_id}`}
      onClick={() => onOpenSession(hit.entity_id)}
    >
      {selecting && (
        <input
          type="checkbox"
          className="mr-2 shrink-0"
          checked={selected}
          data-testid={`session-select-${hit.entity_id}`}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggleSelect(ref)}
          aria-label={`Select ${hit.title}`}
        />
      )}
      <div className="min-w-0 truncate font-medium">{hit.title}</div>
    </div>
  );
}

function BrowseRow({
  row,
  view,
  workItemsBySession,
  collapsedKeys,
  selectedIds,
  selecting,
  activeSessionId,
  deleting,
  onToggleCollapsed,
  onToggleSelect,
  onSelectGroup,
  onOpenSession,
  onDeleteRow,
}: {
  row: SessionListFlatRow<Session>;
  view: SessionListView<Session> | null;
  workItemsBySession: ReadonlyMap<string, SessionWorkItem[]>;
  collapsedKeys: ReadonlySet<string>;
  selectedIds: ReadonlySet<string>;
  selecting: boolean;
  activeSessionId: string | undefined;
  deleting: boolean;
  onToggleCollapsed: (key: string) => void;
  onToggleSelect: (ref: SessionRef) => void;
  onSelectGroup: (refs: SessionRef[]) => void;
  onOpenSession: (sessionId: string) => void;
  onDeleteRow: (
    sessionId: string,
    workspaceId: string | null | undefined,
    event: MouseEvent,
  ) => void;
}) {
  if (row.type === 'group' || row.type === 'subgroup') {
    const expanded = !collapsedKeys.has(row.key);
    const loadedRefs =
      row.type === 'group' && view ? selectionInGroup(view, row.key) : [];
    const allSelected =
      loadedRefs.length > 0 && loadedRefs.every((ref) => selectedIds.has(ref.session_id));
    const nestedLevel = row.type === 'subgroup' ? 1 : 0;
    return (
      <div
        data-testid={row.type === 'group' ? `session-group-${row.key}` : `session-subgroup-${row.key}`}
        className="flex items-center text-xs"
        style={{ paddingLeft: TREE_INDENT * nestedLevel }}
      >
        {selecting && row.type === 'group' && loadedRefs.length > 0 && (
          <input
            type="checkbox"
            className="ml-3 shrink-0"
            checked={allSelected}
            data-testid={`session-group-select-${row.key}`}
            onChange={() => onSelectGroup(loadedRefs)}
            aria-label={`Select group ${row.label}`}
          />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-3 py-1.5 text-left font-medium text-foreground/90 hover:bg-accent/50"
          aria-expanded={expanded}
          data-testid={`session-group-toggle-${row.key}`}
          onClick={() => onToggleCollapsed(row.key)}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(
              'shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className="truncate">{row.label}</span>
          <span className="ml-auto shrink-0 pl-2 font-normal tabular-nums text-muted-foreground">
            {row.loadedCount}
          </span>
        </button>
      </div>
    );
  }

  const active = activeSessionId === row.session.id;
  return (
    <div style={{ paddingLeft: SESSION_INDENT_BASE + TREE_INDENT * row.depth }}>
      <SessionRow
        session={asAgentSession(row.session)}
        workItems={workItemsBySession.get(row.session.id)}
        isActive={active}
        selecting={selecting}
        selected={selectedIds.has(row.session.id)}
        onToggleSelect={() =>
          onToggleSelect({
            session_id: row.session.id,
            workspace_id: row.session.workspace_id ?? null,
          })
        }
        onSelect={onOpenSession}
        onDelete={onDeleteRow}
        deleting={deleting}
      />
    </div>
  );
}

const TREE_INDENT = 16;
const SESSION_INDENT_BASE = 8;
